// Cross-tenant lijst van Partners-records (TOSS, FIX, etc.).
// Gebruikt door admin-flows zoals NewProjectModal om sub-projects aan een
// partner te koppelen.
//
// Veiligheid: Firestore-rules kunnen `list` op de Partners-collectie blokkeren
// terwijl `get` op specifieke slugs wél is toegestaan. We proberen eerst de
// snapshot-listener; als die faalt, vallen we terug op individuele `.doc(slug).get()`
// reads voor een set bekende slugs.
//
// Spec: docs/features/onboarding-flow.md "Netwerk- en partner-context".

import { useEffect, useState } from 'react'
import { db } from '../firebase/config'

// Bekende partner doc-IDs (in seedPartners.js geseed). Bij `list`-permission-error
// vallen we terug op individuele get's op deze IDs. Let op: doc-IDs en `.slug`
// kunnen uit elkaar lopen — beheerders mogen `.slug` later wijzigen zonder de
// docs te hernoemen. Voor lookups op slug-veld: zie `usePartner.js`.
const KNOWN_SLUGS = ['toss', 'fix']

const sortByName = (docs) => [...docs].sort((a, b) => (a.name || '').localeCompare(b.name || ''))

const useAvailablePartners = () => {

    const [partners, setPartners] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {

        let cancelled = false

        const fallbackByGet = async () => {
            try {
                const snaps = await Promise.all(
                    KNOWN_SLUGS.map((slug) => db.collection('Partners').doc(slug).get())
                )
                if (cancelled) return
                const docs = []
                snaps.forEach((s) => {
                    if (s.exists) docs.push({ ...s.data(), docid: s.id })
                })
                setPartners(sortByName(docs))
                setError(null)
            } catch (err) {
                if (cancelled) return
                console.warn('useAvailablePartners fallback-get faalde', err?.message || err)
                setError(err?.message || 'Kon partners niet ophalen')
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        const unsub = db.collection('Partners').onSnapshot(
            (snap) => {
                if (cancelled) return
                const docs = []
                snap.forEach((doc) => {
                    docs.push({ ...doc.data(), docid: doc.id })
                })
                setPartners(sortByName(docs))
                setError(null)
                setLoading(false)
            },
            (err) => {
                if (cancelled) return
                console.warn('useAvailablePartners list-error, val terug op get-by-slug', err?.message || err)
                // Fallback: probeer individuele gets — sommige rules blokkeren
                // alleen `list`, niet `get`.
                fallbackByGet()
            }
        )

        return () => {
            cancelled = true
            unsub()
        }

    }, [])

    return { partners, loading, error }

}

export default useAvailablePartners
